import { useState } from "react";
import { SectionCard } from "./SectionCard";
import { api, abbrevVendedor, fmtCliente } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, Trash2, X, MapPin } from "lucide-react";
import { toast } from "sonner";

const empty = { fecha: "", cliente: "", motivo: "", acciones: "", conclusiones: "", vendedor: "" };

export default function Visitas({ items, reload, canEdit, vendedores = [], vendedorActual }) {
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const startNew = () => {
    setEditing(null);
    setForm({ ...empty, vendedor: vendedorActual && vendedorActual !== "__all__" ? vendedorActual : "" });
    setOpen(!open);
  };

  const startEdit = (v) => {
    setEditing(v.id);
    setForm({ fecha: v.fecha, cliente: v.cliente, motivo: v.motivo, acciones: v.acciones, conclusiones: v.conclusiones, vendedor: v.vendedor || "" });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/visitas/${editing}`, form);
      else await api.post("/visitas", form);
      toast.success(editing ? "Visita actualizada" : "Visita registrada");
      setForm(empty);
      setEditing(null);
      setOpen(false);
      reload();
    } catch (err) {
      toast.error("No se pudo guardar la visita");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/visitas/${id}`);
    toast.success("Visita eliminada");
    reload();
  };

  return (
    <SectionCard
      id="visitas"
      testid="section-visitas"
      num="5"
      title="Registro de Visitas del Coordinador"
      description="Registre y detalle las visitas realizadas a los clientes durante el mes de análisis."
      action={
        canEdit && (
        <Button
          data-testid="open-visita-form"
          onClick={startNew}
          className="bg-emerald-700 hover:bg-emerald-800 h-9"
        >
          {open ? <X className="h-4 w-4 mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />}
          {open ? "Cerrar" : "Nueva Visita"}
        </Button>
        )
      }
    >
      {open && canEdit && (
        <form onSubmit={submit} className="mb-5 p-4 rounded-lg border border-emerald-100 bg-emerald-50/50 grid sm:grid-cols-2 gap-3 animate-fade-up">
          <div className="space-y-1.5">
            <Label>Fecha</Label>
            <Input type="date" data-testid="visita-fecha" value={form.fecha} onChange={set("fecha")} required />
          </div>
          <div className="space-y-1.5">
            <Label>Vendedor</Label>
            <select
              data-testid="visita-vendedor"
              value={form.vendedor}
              onChange={set("vendedor")}
              required
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <option value="">Selecciona un vendedor…</option>
              {vendedores.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Cliente</Label>
            <Input data-testid="visita-cliente" value={form.cliente} onChange={set("cliente")} placeholder="Nombre del cliente" required />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Motivo</Label>
            <Input data-testid="visita-motivo" value={form.motivo} onChange={set("motivo")} placeholder="Motivo de la visita" required />
          </div>
          <div className="space-y-1.5">
            <Label>Acciones Realizadas</Label>
            <Textarea data-testid="visita-acciones" value={form.acciones} onChange={set("acciones")} rows={2} required />
          </div>
          <div className="space-y-1.5">
            <Label>Conclusiones / Acuerdos</Label>
            <Textarea data-testid="visita-conclusiones" value={form.conclusiones} onChange={set("conclusiones")} rows={2} required />
          </div>
          <div className="sm:col-span-2">
            <Button type="submit" data-testid="add-visita-submit-button" disabled={saving} className="bg-emerald-700 hover:bg-emerald-800">
              {editing ? "Actualizar Visita" : "Guardar Visita"}
            </Button>
          </div>
        </form>
      )}

      <div className="overflow-x-auto rounded-lg border border-slate-100">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <th className="text-left px-4 py-3 font-semibold">Fecha</th>
              <th className="text-left px-4 py-3 font-semibold">Vendedor</th>
              <th className="text-left px-4 py-3 font-semibold">Cliente</th>
              <th className="text-left px-4 py-3 font-semibold">Motivo</th>
              <th className="text-left px-4 py-3 font-semibold">Acciones</th>
              <th className="text-left px-4 py-3 font-semibold">Conclusiones</th>
              {canEdit && <th className="text-right px-4 py-3 font-semibold">Acc.</th>}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Sin visitas registradas.</td></tr>
            )}
            {items.map((v) => (
              <tr key={v.id} data-testid={`visita-row-${v.id}`} className="border-t border-slate-100 hover:bg-emerald-50/40 align-top">
                <td className="px-4 py-2.5 font-mono text-xs text-slate-600 whitespace-nowrap">{v.fecha}</td>
                <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap" title={v.vendedor || ""}>{v.vendedor ? abbrevVendedor(v.vendedor) : "—"}</td>
                <td className="px-4 py-2.5 font-medium text-slate-800">
                  <span className="flex items-center gap-1.5" title={v.cliente}><MapPin className="h-3.5 w-3.5 text-emerald-600" /><span className="truncate" style={{ maxWidth: "240px" }}>{fmtCliente(v.cliente)}</span></span>
                </td>
                <td className="px-4 py-2.5 text-slate-600 max-w-[160px]">{v.motivo}</td>
                <td className="px-4 py-2.5 text-slate-600 max-w-[220px]">{v.acciones}</td>
                <td className="px-4 py-2.5 text-slate-600 max-w-[220px]">{v.conclusiones}</td>
                {canEdit && (
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  <button onClick={() => startEdit(v)} data-testid={`edit-visita-${v.id}`} className="p-1.5 text-slate-400 hover:text-emerald-700"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(v.id)} data-testid={`delete-visita-${v.id}`} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}
